<div class="flex flex-col h-full bg-slate-50 relative overflow-hidden">
    <!-- Header -->
    <header class="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between shrink-0">
        <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-600">
                <lucide-icon [img]="icons.Settings" class="w-6 h-6"></lucide-icon>
            </div>
            <div>
                <h1 class="text-xl font-bold text-slate-800">Configurações</h1>
                <p class="text-xs text-slate-500 font-medium">Gerencie parâmetros e usuários do sistema</p>
            </div>
        </div>
    </header>

    <!-- Tabs Navigation -->
    <div class="bg-white border-b border-slate-200 px-6 flex gap-6 sticky top-0 z-10">
        <button (click)="activeTab.set('parameters')"
            class="relative py-4 text-sm font-semibold transition-colors flex items-center gap-2"
            [class.text-indigo-600]="activeTab() === 'parameters'"
            [class.text-slate-500]="activeTab() !== 'parameters'">
            <lucide-icon [img]="icons.Sliders" class="w-4 h-4"></lucide-icon>
            Parâmetros
            <div *ngIf="activeTab() === 'parameters'"
                class="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600 rounded-t-full transition-all"></div>
        </button>
        <button (click)="activeTab.set('users')"
            class="relative py-4 text-sm font-semibold transition-colors flex items-center gap-2"
            [class.text-indigo-600]="activeTab() === 'users'" [class.text-slate-500]="activeTab() !== 'users'">
            <lucide-icon [img]="icons.Users" class="w-4 h-4"></lucide-icon>
            Usuários
            <div *ngIf="activeTab() === 'users'"
                class="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600 rounded-t-full transition-all"></div>
        </button>
    </div>

    <!-- Content Area -->
    <div class="flex-1 overflow-hidden p-6 relative min-h-0 flex flex-col">

        <!-- PARAMETERS TAB -->
        <div *ngIf="activeTab() === 'parameters'" class="animate-fade-in-up flex-1 min-h-0 flex flex-col">
            <div
                class="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden flex flex-col flex-1 min-h-0">
                <!-- Header -->
                <div class="p-6 border-b border-slate-100 shrink-0 flex items-center justify-between">
                    <div>
                        <h2 class="text-lg font-bold text-slate-800 flex items-center gap-2">
                            <lucide-icon [img]="icons.Sliders" class="w-5 h-5 text-indigo-500"></lucide-icon>
                            Parâmetros do Sistema
                        </h2>
                        <p class="text-sm text-slate-500 mt-1">Gerencie as configurações globais de execução.</p>
                    </div>
                    <button (click)="saveAllParameters()" [disabled]="isLoadingParameters()"
                        class="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-lg shadow-sm transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                        @if (isLoadingParameters()) {
                        <lucide-icon [img]="icons.Loader" class="w-4 h-4 animate-spin"></lucide-icon>
                        } @else {
                        <lucide-icon [img]="icons.Save" class="w-4 h-4"></lucide-icon>
                        }
                        Salvar Alterações
                    </button>
                </div>

                <!-- Table -->
                <div class="overflow-auto flex-1 p-0">
                    <table class="w-full text-left border-collapse">
                        <thead class="bg-slate-50 sticky top-0 z-10 shadow-sm">
                            <tr>
                                <th
                                    class="p-4 text-xs font-bold text-slate-500 uppercase tracking-wider border-b border-slate-200 w-[60%]">
                                    Parâmetro</th>
                                <th
                                    class="p-4 text-xs font-bold text-slate-500 uppercase tracking-wider border-b border-slate-200 w-[40%]">
                                    Valor</th>
                            </tr>
                        </thead>
                        <tbody class="divide-y divide-slate-100">
                            <tr *ngFor="let param of parameters()" class="hover:bg-slate-50 transition-colors">
                                <td class="p-4">
                                    <p class="text-sm font-bold text-slate-800">{{ param.displayName }}</p>
                                    <p class="text-xs text-slate-500 mt-0.5">{{ param.description }}</p>
                                    <p class="text-[10px] font-mono text-slate-400 mt-1 select-all">{{
                                        param.parameterKey }}</p>
                                </td>
                                <td class="p-4">
                                    <input type="text" [value]="param.parameterValue"
                                        (input)="updateParameterValue(param, $event)"
                                        class="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none text-sm font-medium transition-all">
                                </td>
                            </tr>
                            <tr *ngIf="parameters().length === 0 && !isLoadingParameters()">
                                <td colspan="2" class="p-8 text-center text-slate-500 text-sm">
                                    Nenhum parâmetro encontrado.
                                </td>
                            </tr>
                            <tr *ngIf="isLoadingParameters()">
                                <td colspan="2" class="p-8 text-center text-slate-500 text-sm">
                                    <div class="flex flex-col items-center gap-2">
                                        <lucide-icon [img]="icons.Loader"
                                            class="w-6 h-6 animate-spin text-indigo-500"></lucide-icon>
                                        <span>Carregando parâmetros...</span>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <!-- USERS TAB -->
        <div *ngIf="activeTab() === 'users'" class="animate-fade-in-up flex-1 min-h-0 flex flex-col">
            <div class="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden flex flex-col flex-1 min-h-0">
                <!-- Toolbar -->
                <div class="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                    <div>
                        <h3 class="font-bold text-slate-800">Gerenciamento de Usuários</h3>
                        <p class="text-xs text-slate-500">{{ users().length }} usuários cadastrados</p>
                    </div>
                    <button (click)="openUserModal()"
                        class="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg shadow-sm transition-colors flex items-center gap-1.5">
                        <lucide-icon [img]="icons.Plus" class="w-3.5 h-3.5"></lucide-icon>
                        Novo Usuário
                    </button>
                </div>

                <!-- Table -->
                <div class="overflow-auto flex-1">
                    <table class="w-full text-left border-collapse">
                        <thead class="bg-slate-50 sticky top-0 z-10 shadow-sm">
                            <tr>
                                <th
                                    class="p-3 text-xs font-bold text-slate-500 uppercase tracking-wider border-b border-slate-200">
                                    Nome</th>
                                <th
                                    class="p-3 text-xs font-bold text-slate-500 uppercase tracking-wider border-b border-slate-200">
                                    E-mail</th>
                                <th
                                    class="p-3 text-xs font-bold text-slate-500 uppercase tracking-wider border-b border-slate-200">
                                    CPF</th>
                                <th
                                    class="p-3 text-xs font-bold text-slate-500 uppercase tracking-wider border-b border-slate-200">
                                    Status</th>
                                <th
                                    class="p-3 text-xs font-bold text-slate-500 uppercase tracking-wider border-b border-slate-200">
                                    Perfil</th>
                                <th
                                    class="p-3 text-xs font-bold text-slate-500 uppercase tracking-wider border-b border-slate-200 text-right">
                                    Ações</th>
                            </tr>
                        </thead>
                        <tbody class="divide-y divide-slate-100">
                            <tr *ngFor="let user of users()" class="hover:bg-slate-50 transition-colors group">
                                <td class="p-3 text-sm font-medium text-slate-800">{{ user.name }}</td>
                                <td class="p-3 text-sm text-slate-600">{{ user.email }}</td>
                                <td class="p-3 text-sm text-slate-600">{{ user.cpf || '-' }}</td>
                                <td class="p-3">
                                    <span
                                        class="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
                                        [class.bg-emerald-100]="user.active" [class.text-emerald-700]="user.active"
                                        [class.bg-slate-100]="!user.active" [class.text-slate-600]="!user.active">
                                        {{ user.active ? 'Ativo' : 'Inativo' }}
                                    </span>
                                </td>
                                <td class="p-3">
                                    <span class="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
                                        [class.bg-purple-100]="user.role === 'Admin'"
                                        [class.text-purple-700]="user.role === 'Admin'"
                                        [class.bg-blue-100]="user.role === 'Operator'"
                                        [class.text-blue-700]="user.role === 'Operator'"
                                        [class.bg-slate-100]="user.role === 'Viewer'"
                                        [class.text-slate-600]="user.role === 'Viewer'"
                                        [class.bg-emerald-100]="user.role === 'ProductSyncOperator'"
                                        [class.text-emerald-700]="user.role === 'ProductSyncOperator'"
                                        [class.bg-teal-100]="user.role === 'ProductSyncViewer'"
                                        [class.text-teal-700]="user.role === 'ProductSyncViewer'"
                                        [class.bg-orange-100]="user.role === 'AlloraSyncOperator'"
                                        [class.text-orange-700]="user.role === 'AlloraSyncOperator'"
                                        [class.bg-amber-100]="user.role === 'AlloraSyncViewer'"
                                        [class.text-amber-700]="user.role === 'AlloraSyncViewer'">
                                        {{ getRoleLabel(user.role) }}
                                    </span>
                                </td>
                                <td class="p-3 text-right">
                                    <div
                                        class="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button (click)="openUserModal(user)"
                                            class="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-slate-100 rounded transition-colors"
                                            title="Editar">
                                            <lucide-icon [img]="icons.Edit" class="w-4 h-4"></lucide-icon>
                                        </button>
                                        <button (click)="deleteUser(user.uuid)"
                                            class="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded transition-colors"
                                            title="Excluir">
                                            <lucide-icon [img]="icons.Trash2" class="w-4 h-4"></lucide-icon>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                            <tr *ngIf="users().length === 0 && !isLoadingUsers()">
                                <td colspan="6" class="p-6 text-center text-slate-500 text-sm">
                                    Nenhum usuário encontrado.
                                </td>
                            </tr>
                            <tr *ngIf="isLoadingUsers()">
                                <td colspan="6" class="p-6 text-center text-slate-500 text-sm">
                                    Carregando usuários...
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </div>

    <!-- User Modal Overlay -->
    <div *ngIf="isUserModalOpen()" class="fixed inset-0 z-50 flex items-center justify-center p-4">
        <!-- Backdrop -->
        <div class="absolute inset-0 bg-slate-900/40 backdrop-blur-sm transition-opacity" (click)="closeUserModal()">
        </div>

        <!-- Modal -->
        <div
            class="bg-white rounded-xl shadow-2xl w-full max-w-md relative z-10 animate-scale-in overflow-hidden flex flex-col max-h-[90vh]">
            <div class="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50">
                <h3 class="text-lg font-bold text-slate-800">
                    {{ editingUser() ? 'Editar Usuário' : 'Novo Usuário' }}
                </h3>
                <button (click)="closeUserModal()" class="text-slate-400 hover:text-slate-600 transition-colors">
                    <lucide-icon [img]="icons.X" class="w-5 h-5"></lucide-icon>
                </button>
            </div>

            <div class="p-6 overflow-y-auto">
                <form [formGroup]="userForm" class="space-y-4">
                    <!-- Name -->
                    <div>
                        <label class="block text-xs font-bold text-slate-500 uppercase mb-1">Nome Completo</label>
                        <input type="text" formControlName="name"
                            class="w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none transition-all text-sm"
                            placeholder="Ex: João Silva">
                    </div>

                    <!-- Email -->
                    <div>
                        <label class="block text-xs font-bold text-slate-500 uppercase mb-1">E-mail</label>
                        <input type="email" formControlName="email"
                            class="w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none transition-all text-sm"
                            placeholder="Ex: joao@empresa.com">
                    </div>

                    <!-- CPF -->
                    <div>
                        <label class="block text-xs font-bold text-slate-500 uppercase mb-1">CPF</label>
                        <input type="text" formControlName="cpf" maxlength="11"
                            class="w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none transition-all text-sm"
                            placeholder="000.000.000-00">
                    </div>

                    <!-- Role -->
                    <div>
                        <label class="block text-xs font-bold text-slate-500 uppercase mb-1">Perfil de Acesso</label>
                        <select formControlName="role"
                            class="w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none transition-all text-sm bg-white">
                            <option value="Viewer">Visualizador</option>
                            <option value="Operator">Operador</option>
                            <option value="Admin">Administrador</option>
                            <option value="ProductSyncViewer">Visualizador de Replicação</option>
                            <option value="ProductSyncOperator">Operador de Replicação</option>
                            <option value="AlloraSyncViewer">Visualizador Allora</option>
                            <option value="AlloraSyncOperator">Operador Allora</option>
                        </select>
                    </div>

                    <!-- Active -->
                    <div class="flex items-center gap-3">
                        <div
                            class="relative inline-block w-10 h-6 align-middle select-none transition duration-200 ease-in">
                            <input type="checkbox" formControlName="active" id="activeToggle"
                                class="toggle-checkbox absolute block w-4 h-4 rounded-full bg-white border-4 appearance-none cursor-pointer translate-x-1 checked:translate-x-5 checked:border-indigo-600 top-1 transition-transform" />
                            <label for="activeToggle"
                                class="toggle-label block overflow-hidden h-6 rounded-full bg-slate-200 cursor-pointer checked:bg-indigo-600 transition-colors border border-slate-300"></label>
                        </div>
                        <label for="activeToggle" class="text-sm font-medium text-slate-700 cursor-pointer">Usuário
                            Ativo</label>
                    </div>

                    <!-- Password (Conditionals omitted for simplicity, always show for now) -->
                    <div>
                        <label class="block text-xs font-bold text-slate-500 uppercase mb-1">Senha</label>
                        <input type="password" formControlName="password"
                            class="w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none transition-all text-sm"
                            placeholder="********">
                        <p class="text-[10px] text-slate-400 mt-1" *ngIf="editingUser()">Deixe em branco para manter a
                            atual</p>
                    </div>
                </form>
            </div>

            <div class="px-6 py-4 border-t border-slate-100 bg-slate-50 flex justify-end gap-2">
                <button (click)="closeUserModal()"
                    class="px-4 py-2 text-sm font-semibold text-slate-600 hover:text-slate-800 hover:bg-slate-200 rounded-lg transition-colors">
                    Cancelar
                </button>
                <button (click)="saveUser()" [disabled]="userForm.invalid"
                    class="px-4 py-2 text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                    {{ editingUser() ? 'Salvar Alterações' : 'Criar Usuário' }}
                </button>
            </div>
        </div>
    </div>

    <!-- Generic Modal -->
    <div *ngIf="modalState()"
        class="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[100] flex items-center justify-center p-4 animate-fade-in">
        <div class="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 overflow-hidden">
            <div class="flex items-center gap-4 mb-4">
                <div [class]="'w-12 h-12 rounded-full flex items-center justify-center shrink-0 ' + 
                    (modalState()?.type === 'success' ? 'bg-emerald-100 text-emerald-600' : 
                     modalState()?.type === 'error' ? 'bg-rose-100 text-rose-600' : 'bg-amber-100 text-amber-600')">
                    <lucide-icon [img]="modalState()?.type === 'success' ? icons.CheckCircle : 
                                       modalState()?.type === 'error' ? icons.AlertOctagon : icons.AlertCircle"
                        class="w-6 h-6"></lucide-icon>
                </div>
                <h3 class="text-lg font-bold text-slate-800">{{ modalState()?.title }}</h3>
            </div>

            <p class="text-sm text-slate-600 mb-8 leading-relaxed">
                {{ modalState()?.message }}
            </p>

            <div class="flex justify-end gap-3">
                <button *ngIf="modalState()?.cancelLabel" (click)="closeModal()"
                    class="px-4 py-2 text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors">
                    {{ modalState()?.cancelLabel }}
                </button>

                <button (click)="modalState()?.onConfirm ? modalState()?.onConfirm!() : closeModal(); closeModal()"
                    [class]="'px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors shadow-sm ' +
                    (modalState()?.type === 'success' ? 'bg-emerald-600 hover:bg-emerald-700' :
                     modalState()?.type === 'error' ? 'bg-rose-600 hover:bg-rose-700' : 'bg-indigo-600 hover:bg-indigo-700')">
                    {{ modalState()?.confirmLabel }}
                </button>
            </div>
        </div>
    </div>
</div>